/**
 * Calculate the optimal zoom level to fit all selected assets in view
 * @param {Array} coordinates - Array of [longitude, latitude] pairs
 * @param {Object} mapContainer - Reference to the map container element
 * @param {number} padding - Padding in pixels around the bounds (default: 50)
 * @returns {number} - Optimal zoom level
 */
export function calculateOptimalZoom(coordinates, mapContainer, padding = 50) {
  if (!coordinates || coordinates.length === 0) {
    return 15; // Default zoom
  }

  // Single asset case
  if (coordinates.length === 1) {
    return 18; // Close zoom for single asset
  }

  // Calculate bounds
  const bounds = coordinates.reduce(
    (acc, coord) => {
      return {
        minLng: Math.min(acc.minLng, coord[0]),
        maxLng: Math.max(acc.maxLng, coord[0]),
        minLat: Math.min(acc.minLat, coord[1]),
        maxLat: Math.max(acc.maxLat, coord[1]),
      };
    },
    {
      minLng: Infinity,
      maxLng: -Infinity,
      minLat: Infinity,
      maxLat: -Infinity,
    }
  );

  // Get map container dimensions
  const mapWidth = mapContainer.offsetWidth - padding * 2;
  const mapHeight = mapContainer.offsetHeight - padding * 2;

  // Calculate latitude and longitude spans
  const lngSpan = bounds.maxLng - bounds.minLng;
  const latSpan = bounds.maxLat - bounds.minLat;

  // Handle edge case where all points are very close
  if (lngSpan < 0.0001 && latSpan < 0.0001) {
    return 18; // Very close zoom for clustered assets
  }

  // Calculate zoom based on longitude span
  // MapTiler/Mapbox uses Web Mercator projection
  const lngZoom = Math.log2(360 / lngSpan) - Math.log2(mapWidth / 256);

  // Calculate zoom based on latitude span
  // Account for Mercator projection distortion
  const centerLat = (bounds.minLat + bounds.maxLat) / 2;
  const latZoom =
    Math.log2(180 / latSpan / Math.cos((centerLat * Math.PI) / 180)) - Math.log2(mapHeight / 256);

  // Use the more restrictive (lower) zoom level
  const optimalZoom = Math.min(lngZoom, latZoom);

  // Clamp zoom between reasonable bounds
  return Math.max(12, Math.min(20, Math.floor(optimalZoom)));
}

/**
 * Calculate bounds for fitBounds method (alternative approach)
 * @param {Array} coordinates - Array of [longitude, latitude] pairs
 * @returns {Array} - [[minLng, minLat], [maxLng, maxLat]]
 */
export function calculateBounds(coordinates) {
  if (!coordinates || coordinates.length === 0) {
    return null;
  }

  const bounds = coordinates.reduce(
    (acc, coord) => {
      return {
        minLng: Math.min(acc.minLng, coord[0]),
        maxLng: Math.max(acc.maxLng, coord[0]),
        minLat: Math.min(acc.minLat, coord[1]),
        maxLat: Math.max(acc.maxLat, coord[1]),
      };
    },
    {
      minLng: Infinity,
      maxLng: -Infinity,
      minLat: Infinity,
      maxLat: -Infinity,
    }
  );

  return [
    [bounds.minLng, bounds.minLat],
    [bounds.maxLng, bounds.maxLat],
  ];
}
